/** @jsxImportSource ../i18n */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, RefreshCw, Trash2 } from 'lucide-react';
import api from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { benchmarkColor, periodReturn, rebaseOnDates } from '../services/benchmarks';
import { BrokerGate, useBroker } from '../components/shell/Broker';
import { ReturnLineChart, TIME_RANGES, ValueAreaChart } from '../components/charts';
import { cx } from '../theme/cx';
import { formatDate, formatNumber, formatPercent } from '../theme/format';
import {
  Badge, Banner, Button, Card, CardHeader, Delta, EmptyState, IconButton, Money, PageHeader, Percent, SegmentedControl,
  Skeleton, SkeletonStat, Spinner, Stat, SymbolMark,
} from '../components/ui';

const CHART_VIEWS = [
  { value: 'value', label: 'Valeur' },
  { value: 'return', label: 'Rendement' },
];

const errorText = (error) => error?.response?.data?.error || 'Le serveur n’a pas répondu.';

const PerformanceSkeleton = () => (
  <div className="space-y-6" role="status">
    <span className="sr-only">Chargement de la performance…</span>
    <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-5">
      {Array.from({ length: 5 }, (_, i) => <SkeletonStat key={i} className={i === 0 ? 'col-span-2 xl:col-span-1' : undefined} />)}
    </div>
    <Card aria-hidden="true">
      <Skeleton className="h-4 w-40" />
      <Skeleton className="mt-6 h-[220px] w-full sm:h-[320px]" />
    </Card>
  </div>
);

const Row = ({ label, children, className }) => (
  <div className={cx('flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0', className)}>
    <dt className="text-sm text-fg-muted">{label}</dt>
    <dd className="num text-right text-[15px] font-semibold text-fg">{children}</dd>
  </div>
);

const Performer = ({ label, symbol, pl }) => (
  <div className="py-3 first:pt-0 last:pb-0">
    <dt className="eyebrow">{label}</dt>
    <dd className="mt-2 flex items-center justify-between gap-3">
      <span className="flex min-w-0 items-center gap-3">
        <SymbolMark symbol={symbol} size="sm" />
        <span className="truncate font-medium text-fg">{symbol || '—'}</span>
      </span>
      <Money value={pl} decimals={0} sign colorize className="font-semibold" />
    </dd>
  </div>
);

const BENCHMARKS_KEY = 'ui.benchmarks';

const readSelected = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(BENCHMARKS_KEY));
    if (Array.isArray(stored)) return stored;
  } catch {
    // ignore unreadable storage
  }
  return ['cac40'];
};

const BenchmarkToggles = ({ series, selected, onToggle }) => (
  <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Indices de comparaison">
    {series.map((b) => {
      const on = selected.includes(b.id);
      return (
        <button
          key={b.id}
          type="button"
          aria-pressed={on}
          disabled={Boolean(b.error)}
          title={b.error || undefined}
          onClick={() => onToggle(b.id)}
          className={cx(
            'inline-flex h-8 items-center gap-2 rounded-full border px-3 text-[13px] font-medium transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-45',
            on ? 'border-line-strong bg-surface-3 text-fg' : 'border-line text-fg-muted hover:border-line-strong hover:text-fg',
          )}
        >
          <span aria-hidden="true" className={cx('h-0.5 w-4 rounded-full', !on && 'opacity-50')} style={{ backgroundColor: benchmarkColor(b.id) }} />
          {b.label}
        </button>
      );
    })}
  </div>
);

const PerformanceContent = ({ data, view, onView, loading, benchmarks, selected, onToggleBenchmark }) => {
  const dates = useMemo(() => (data.account_value || []).map((p) => p.date), [data]);
  const rebased = useMemo(() => Object.fromEntries((benchmarks || []).map((b) => [b.id, rebaseOnDates(b.points, dates)])), [benchmarks, dates]);
  const chartData = useMemo(() => (data.account_value || []).map((point, index) => ({
    date: point.date,
    value: point.value,
    return: data.return_percent?.[index]?.value || 0,
    ...Object.fromEntries(Object.entries(rebased).map(([id, values]) => [`bm_${id}`, values[index]])),
  })), [data, rebased]);
  const shown = (benchmarks || []).filter((b) => selected.includes(b.id) && !b.error && periodReturn(rebased[b.id] || []) !== null);
  const portfolioReturn = data.return_percent?.length ? data.return_percent[data.return_percent.length - 1].value : null;

  const indicators = [
    { label: 'Rendement annualisé', value: formatPercent(data.annualized_return) },
    { label: 'Volatilité', value: formatPercent(data.volatility, { sign: false }) },
    { label: 'Ratio de Sharpe', value: formatNumber(data.sharpe_ratio, { decimals: 2 }) },
    { label: 'Drawdown max', value: formatPercent(data.max_drawdown == null ? null : -data.max_drawdown), className: 'text-loss-400' },
    { label: 'Jours de bourse', value: formatNumber(data.trading_days) },
    { label: 'Dividendes perçus', value: <Money value={data.dividend_income} decimals={0} />, className: 'text-gain-400' },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-5">
        <Stat
          className="col-span-2 xl:col-span-1"
          label="Valeur actuelle"
          value={<Money value={data.ending_value} decimals={0} animate />}
          hint={<>Flux nets <Money value={data.net_transfers} decimals={0} sign /></>}
        />
        <Stat
          label="Variation de valeur"
          value={<Money value={data.value_change} decimals={0} sign colorize />}
          delta={<Delta value={data.value_change_percent} decimals={2} />}
        />
        <Stat
          label="Rendement total"
          value={<Percent value={data.total_return_percent} decimals={2} colorize />}
          hint={<Money value={data.total_return} decimals={0} sign />}
        />
        <Stat
          label="Flux nets"
          value={<Money value={data.net_transfers} decimals={0} sign />}
          hint={<>Versements <Money value={data.deposits} decimals={0} /> · Retraits <Money value={data.withdrawals} decimals={0} /></>}
        />
        <Stat
          label="+/- value totale"
          value={<Money value={data.total_pl} decimals={0} sign colorize />}
          hint={<>Réalisée <Money value={data.realized_pl} decimals={0} sign /> · Latente <Money value={data.unrealized_pl} decimals={0} sign /></>}
        />
      </div>

      <Card>
        <CardHeader
          title={view === 'value' ? 'Valeur du compte' : 'Rendement cumulé'}
          description={view === 'value' ? 'Valorisation totale, liquidités comprises' : 'Rendement pondéré dans le temps, comparé aux indices'}
          actions={<SegmentedControl ariaLabel="Type de graphique" options={CHART_VIEWS} value={view} onChange={onView} />}
        />
        {view === 'return' && benchmarks?.length > 0 && (
          <div className="-mt-1 mb-5 space-y-3">
            <BenchmarkToggles series={benchmarks} selected={selected} onToggle={onToggleBenchmark} />
            {shown.length > 0 && portfolioReturn !== null && (
              <dl className="flex flex-wrap gap-x-6 gap-y-2 text-sm" data-testid="benchmark-summary">
                <div className="flex items-baseline gap-2">
                  <dt className="text-fg-muted">Votre PEA</dt>
                  <dd><Percent value={portfolioReturn} decimals={2} colorize className="font-semibold" /></dd>
                </div>
                {shown.map((b) => {
                  const r = periodReturn(rebased[b.id]);
                  const gap = portfolioReturn - r;
                  return (
                    <div key={b.id} className="flex items-baseline gap-2">
                      <dt className="text-fg-muted">{b.label}</dt>
                      <dd className="num text-fg">
                        {formatPercent(r, { decimals: 2 })}
                        <span className={cx('ml-1.5 text-xs', gap >= 0 ? 'text-gain-400' : 'text-loss-400')}>
                          ({formatNumber(gap, { decimals: 2, sign: true })} pts)
                        </span>
                      </dd>
                    </div>
                  );
                })}
              </dl>
            )}
          </div>
        )}
        <div className="relative" aria-busy={loading || undefined}>
          {chartData.length === 0 ? (
            <p className="flex h-[220px] items-center justify-center text-sm text-fg-muted sm:h-[320px]">Aucune donnée sur cette période.</p>
          ) : (
            <div className="h-[220px] sm:h-[320px]">
              {view === 'value'
                ? <ValueAreaChart data={chartData} height="100%" />
                : <ReturnLineChart data={chartData} height="100%" label="Votre PEA" lines={shown.map((bm) => ({ key: `bm_${bm.id}`, label: bm.label, color: benchmarkColor(bm.id) }))} />}
            </div>
          )}
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center rounded-control bg-surface-1/60">
              <Spinner size={20} />
            </div>
          )}
        </div>
        {view === 'return' && shown.length > 0 && (
          <p className="mt-4 text-xs leading-relaxed text-fg-subtle">CAC 40 et Euro Stoxx 50 : indices de prix, hors dividendes. CW8 : ETF capitalisant, dividendes réinvestis.</p>
        )}
      </Card>

      <Card>
        <CardHeader title="Indicateurs" description="Risque et rendement sur la période sélectionnée" />
        <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-control border border-line bg-line sm:grid-cols-3 xl:grid-cols-6">
          {indicators.map((item) => (
            <div key={item.label} className="flex flex-col justify-between bg-surface-1 p-4 sm:p-5">
              <dt className="eyebrow">{item.label}</dt>
              <dd className={cx('num mt-2 text-xl font-semibold tracking-[-0.01em]', item.className || 'text-fg')}>{item.value}</dd>
            </div>
          ))}
        </dl>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader title="Gains et pertes" description="Opérations clôturées" />
          <dl className="divide-y divide-line">
            <Row label="Opérations gagnantes"><span className="text-gain-400">{formatNumber(data.winning_trades)}</span></Row>
            <Row label="Opérations perdantes"><span className="text-loss-400">{formatNumber(data.losing_trades)}</span></Row>
            <Row label="Taux de réussite">{formatPercent(data.win_rate, { sign: false })}</Row>
            <Row label="Gain moyen"><Money value={data.avg_win_amount} colorize /></Row>
            <Row label="Perte moyenne"><Money value={data.avg_loss_amount == null ? null : -data.avg_loss_amount} colorize /></Row>
            <Row label="Facteur de profit">{formatNumber(data.profit_factor, { decimals: 2 })}</Row>
          </dl>
        </Card>
        <Card>
          <CardHeader title="Analyse des positions" description={`${formatNumber(data.positions_count)} position(s) suivie(s)`} />
          <dl className="divide-y divide-line">
            <Performer label="Meilleure valeur" symbol={data.best_performing_symbol} pl={data.best_performing_pl} />
            <Performer label="Moins bonne valeur" symbol={data.worst_performing_symbol} pl={data.worst_performing_pl} />
            <Row label="Meilleure séance"><Percent value={data.best_day_return} decimals={2} colorize /></Row>
            <Row label="Pire séance"><Percent value={data.worst_day_return} decimals={2} colorize /></Row>
          </dl>
        </Card>
      </div>
    </div>
  );
};

const Performance = () => {
  const toast = useToast();
  const { status } = useBroker();
  const connected = status === 'connected';
  const [range, setRange] = useState('1m');
  const [view, setView] = useState('value');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [clearing, setClearing] = useState(false);
  const [benchmarks, setBenchmarks] = useState([]);
  const [selected, setSelected] = useState(readSelected);
  const request = useRef(0);

  const toggleBenchmark = (id) => setSelected((current) => {
    const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
    try { localStorage.setItem(BENCHMARKS_KEY, JSON.stringify(next)); } catch { /* storage unavailable */ }
    return next;
  });

  const load = useCallback(async (forceRefresh = false) => {
    const id = request.current + 1;
    request.current = id;
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/performance/dashboard?range=${range}${forceRefresh ? '&force_refresh=true' : ''}`);
      if (id === request.current) setData(response.data);
    } catch (err) {
      if (id === request.current) setError(errorText(err));
    } finally {
      if (id === request.current) setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    if (connected) load();
  }, [connected, load]);

  // Reference indices load on their own: a sidecar outage never blocks the page.
  useEffect(() => {
    if (!connected) return undefined;
    let alive = true;
    api.get(`/market/benchmarks?range=${range}`)
      .then((res) => { if (alive) setBenchmarks(res.data?.benchmarks || []); })
      .catch(() => { if (alive) setBenchmarks([]); });
    return () => { alive = false; };
  }, [connected, range]);

  const clearCache = async () => {
    setClearing(true);
    try {
      await api.delete('/performance/cache');
      toast.success('Cache vidé', { description: 'Les données sont rechargées depuis le courtier.' });
      load(true);
    } catch (err) {
      toast.error('Impossible de vider le cache', { description: errorText(err) });
    } finally {
      setClearing(false);
    }
  };

  const period = data?.from_date && data?.to_date
    ? `Du ${formatDate(data.from_date)} au ${formatDate(data.to_date)}`
    : 'Rendement, risque et statistiques de vos opérations.';

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Patrimoine"
        title="Performance"
        description={period}
        actions={connected && (
          <>
            {data?.cached && <Badge dot>Données en cache</Badge>}
            <IconButton icon={Trash2} label="Vider le cache" variant="secondary" onClick={clearCache} disabled={clearing || loading} />
            <Button variant="secondary" leadingIcon={RefreshCw} loading={loading && Boolean(data)} onClick={() => load(true)}>
              Actualiser
            </Button>
          </>
        )}
      />
      <BrokerGate>
        <div className="space-y-6">
          <SegmentedControl ariaLabel="Période" options={TIME_RANGES} value={range} onChange={setRange} />
          {error && !data && (
            <Card>
              <EmptyState
                icon={AlertCircle}
                tone="loss"
                title="Impossible de charger la performance"
                description={error}
                action={<Button leadingIcon={RefreshCw} onClick={() => load(true)}>Réessayer</Button>}
              />
            </Card>
          )}
          {error && data && (
            <Banner tone="loss" role="alert" action={<Button size="sm" variant="secondary" onClick={() => load(true)}>Réessayer</Button>}>
              {error}
            </Banner>
          )}
          {!data && !error && <PerformanceSkeleton />}
          {data && <PerformanceContent data={data} view={view} onView={setView} loading={loading} benchmarks={benchmarks} selected={selected} onToggleBenchmark={toggleBenchmark} />}
        </div>
      </BrokerGate>
    </div>
  );
};

export default Performance;
